import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation } from "convex/react";
import { api } from "@repo/backend";
import { useState, useEffect, type ReactNode } from "react";
import { AuctionCard } from "@/components/auction";
import { toAuctionListItem } from "@/hooks/useAuctions";
import { toUserFriendlyErrorMessage } from "@/lib/errors";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
});

type Tab = "registry" | "positions" | "treasury";

const sandboxFundingPresets = [50000, 100_000, 250_000, 500_000] as const;

function ProfilePage() {
  const currentUser = useQuery(api.users.queries.me);
  const updateProfile = useMutation(api.users.mutations.updateProfile);
  const addSandboxFunds = useMutation(api.users.mutations.addSandboxFunds);
  const myBids = useQuery(api.auctions.queries.myBidAuctions, { paginationOpts: { numItems: 10, cursor: null } });
  const myListings = useQuery(api.auctions.queries.listBySeller, currentUser ? { sellerId: currentUser._id, paginationOpts: { numItems: 10, cursor: null } } : "skip");

  const [activeTab, setActiveTab] = useState<Tab>("registry");
  const [name, setName] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [sandboxAmount, setSandboxAmount] = useState("1000");
  const [isFundingSandbox, setIsFundingSandbox] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [sandboxMessage, setSandboxMessage] = useState<string | null>(null);
  const [sandboxError, setSandboxError] = useState<string | null>(null);
  const activeBidAuctions = (myBids?.page ?? []).filter((auction) => auction !== null);

  useEffect(() => {
    if (currentUser) {
      setName(currentUser.name ?? "");
      setImageUrl(currentUser.image ?? "");
    }
  }, [currentUser]);

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <div className="w-px h-12 bg-secondary/60 animate-pulse shadow-[0_0_12px_rgba(241,198,41,0.4)]" />
        <div className="w-8 h-8 rounded-full border-2 border-secondary border-t-transparent animate-spin" />
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-500">
          Fetching Ledger...
        </span>
      </div>
    );
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-8 text-center gap-6">
        <div className="w-px h-12 bg-primary/60 shadow-[0_0_12px_rgba(205,1,1,0.4)]" />
        <p className="text-iron-400 font-body italic text-lg max-w-sm">
          "The archives are closed to those without a seal."
        </p>
        <span className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-500">
          Authentication Required
        </span>
      </div>
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setProfileMessage(null);
    setProfileError(null);
    setIsSaving(true);
    try {
      await updateProfile({
        name: name || undefined,
        imageUrl: imageUrl || undefined,
      });
      setProfileMessage("Your registry has been updated.");
    } catch (error) {
      setProfileError(
        toUserFriendlyErrorMessage(error, "We could not update your registry. Please try again."),
      );
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSandboxFundsSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSandboxMessage(null);
    setSandboxError(null);

    const amount = Number(sandboxAmount);

    if (!Number.isInteger(amount) || amount <= 0) {
      setSandboxError("Enter a whole number greater than zero.");
      return;
    }

    setIsFundingSandbox(true);
    try {
      const result = await addSandboxFunds({ amount });
      setSandboxMessage(`Added ${amount} RP to your sandbox balance. New balance: ${result.goldBalance} RP.`);
      setSandboxAmount("1000");
    } catch (error) {
      setSandboxError(
        toUserFriendlyErrorMessage(
          error,
          "We could not add sandbox funds right now. Please try again.",
        ),
      );
    } finally {
      setIsFundingSandbox(false);
    }
  }

  const sandboxFundingSection = (
    <div className="border border-secondary/20 bg-[linear-gradient(180deg,rgba(241,198,41,0.08),rgba(19,23,31,0.18))] p-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-secondary">Sandbox Funds</p>
          <h3 className="text-lg tracking-[0.2em] font-epic uppercase text-iron-100 mt-2">Top Up Your Test Balance</h3>
          <p className="text-sm text-iron-400 font-body mt-2 max-w-2xl">
            Instantly add RP for local testing, bidding flows, and demo accounts. This section is for sandbox use only.
          </p>
        </div>

        <div className="border border-iron-800 bg-iron-950/60 px-5 py-4 min-w-52">
          <span className="text-[9px] tracking-[0.3em] uppercase font-bold text-iron-500">Available Balance</span>
          <div className="text-2xl text-secondary font-epic tracking-widest mt-2">
            {currentUser.goldBalance} RP
          </div>
        </div>
      </div>

      <div className="h-px bg-secondary/20 my-6" />

      <form onSubmit={handleSandboxFundsSubmit} className="space-y-6">
        <div className="flex flex-wrap gap-3">
          {sandboxFundingPresets.map((amount) => (
            <button
              key={amount}
              type="button"
              onClick={() => {
                setSandboxAmount(String(amount));
                setSandboxMessage(null);
                setSandboxError(null);
              }}
              className={`px-4 py-3 border text-[10px] tracking-[0.25em] uppercase font-bold transition-all ${
                sandboxAmount === String(amount)
                  ? "border-secondary bg-secondary/10 text-secondary"
                  : "border-iron-800 bg-iron-950/50 text-iron-400 hover:border-iron-600 hover:text-iron-200"
              }`}
            >
              +{amount} RP
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1 space-y-3">
            <label htmlFor="sandboxAmount" className="text-[10px] uppercase tracking-[0.3em] text-iron-400 font-bold block">
              Custom Sandbox Amount
            </label>
            <input
              id="sandboxAmount"
              type="number"
              min="1"
              max="100000000"
              step="1"
              inputMode="numeric"
              value={sandboxAmount}
              onChange={(e) => setSandboxAmount(e.target.value)}
              className="w-full bg-iron-950/50 border border-iron-800 px-5 py-4 text-sm text-iron-100 focus:outline-none focus:border-secondary transition-all placeholder:text-iron-700"
              placeholder="1000"
            />
          </div>

          <button
            type="submit"
            disabled={isFundingSandbox}
            className="w-full lg:w-auto px-10 py-4 bg-secondary text-iron-950 font-epic uppercase text-xs tracking-[0.3em] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {isFundingSandbox ? "Funding Sandbox..." : "Add Funds"}
          </button>
        </div>

        {sandboxMessage && (
          <div className="border border-secondary/30 bg-secondary/10 px-4 py-3 text-sm text-secondary">
            {sandboxMessage}
          </div>
        )}

        {sandboxError && (
          <div className="border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-primary">
            {sandboxError}
          </div>
        )}
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* ── Hero Header ── */}
      <div className="border-b border-iron-800 bg-iron-950/40 backdrop-blur-sm px-8 pt-16 pb-12">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div className="flex items-center gap-8">
              <div className="relative group">
                <div className="absolute -inset-1 bg-secondary/20 rounded-full blur group-hover:bg-secondary/30 transition-all" />
                <div className="relative w-32 h-32 rounded-full border-4 border-iron-800 overflow-hidden bg-iron-950 flex items-center justify-center">
                  {imageUrl ? (
                    <img src={imageUrl} alt={name || "Avatar"} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl text-iron-700 font-epic uppercase">
                      {(name || currentUser.email || "?").charAt(0)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                  <div className="w-1 h-6 bg-secondary shadow-[0_0_8px_rgba(241,198,41,0.6)]" />
                  <h1 className="text-4xl tracking-[0.2em] uppercase text-iron-100 font-epic">
                    {name || "Adventurer"}
                  </h1>
                </div>
                <p className="text-[10px] tracking-[0.3em] uppercase font-bold text-iron-500 ml-4">
                  Confirmed Member — {currentUser.email}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex flex-col items-end gap-1">
                <span className="text-[9px] tracking-[0.3em] uppercase font-bold text-iron-500">Current Balance</span>
                <span className="text-2xl text-secondary font-epic tracking-widest leading-none">
                  {currentUser.goldBalance} RP
                </span>
              </div>
              <div className="w-px h-12 bg-iron-800" />
              <div className="flex flex-col items-end gap-1">
                <span className="text-[9px] tracking-[0.3em] uppercase font-bold text-iron-500">Privilege</span>
                <span className="text-sm text-iron-100 uppercase font-bold tracking-[0.2em]">
                  {currentUser.role}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-6xl px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
          {/* ── Sidebar Tabs ── */}
          <div className="lg:col-span-1 flex flex-col gap-2">
            {[
              { id: "registry", label: "Registry Settings" },
              { id: "positions", label: "Active Positions" },
              { id: "treasury", label: "My Treasury" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as Tab)}
                className={`flex items-center justify-between px-6 py-4 border-l-2 text-[10px] tracking-[0.3em] uppercase font-bold transition-all ${
                  activeTab === tab.id
                    ? "border-secondary bg-secondary/5 text-secondary shadow-[inset_4px_0_12px_-4px_rgba(241,198,41,0.2)]"
                    : "border-iron-800 text-iron-500 hover:border-iron-600 hover:text-iron-300"
                }`}
              >
                {tab.label}
                {activeTab === tab.id && <div className="w-1.5 h-1.5 rounded-full bg-secondary shadow-[0_0_5px_rgba(241,198,41,0.8)]" />}
              </button>
            ))}
          </div>

          {/* ── Tab Content ── */}
          <div className="lg:col-span-3">
            {activeTab === "registry" && (
              <div className="space-y-8">
                <div className="border border-iron-800 bg-iron-900/30 p-10">
                  <div className="mb-10">
                    <h2 className="text-xl tracking-[0.2em] font-epic uppercase text-iron-100 mb-2">Member Registry</h2>
                    <p className="text-sm text-iron-500 font-body">Update your status and appearance in the realm's ledger.</p>
                  </div>

                  <form onSubmit={handleSubmit} className="flex flex-col gap-8 max-w-xl">
                    {profileMessage && (
                      <FeedbackBanner tone="success">{profileMessage}</FeedbackBanner>
                    )}

                    {profileError && (
                      <FeedbackBanner tone="error">{profileError}</FeedbackBanner>
                    )}

                    <div className="space-y-3">
                      <label htmlFor="name" className="text-[10px] uppercase tracking-[0.3em] text-iron-400 font-bold block">
                        Display Identity
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-iron-950/50 border border-iron-800 px-5 py-4 text-sm text-iron-100 focus:outline-none focus:border-secondary transition-all placeholder:text-iron-700"
                        placeholder="e.g. Sir Galahad"
                      />
                    </div>

                    <div className="space-y-3">
                      <label htmlFor="imageUrl" className="text-[10px] uppercase tracking-[0.3em] text-iron-400 font-bold block">
                        Portrait URI
                      </label>
                      <input
                        id="imageUrl"
                        type="url"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className="w-full bg-iron-950/50 border border-iron-800 px-5 py-4 text-sm text-iron-100 focus:outline-none focus:border-secondary transition-all placeholder:text-iron-700"
                        placeholder="https://..."
                      />
                    </div>

                    <div className="pt-4">
                      <button
                        type="submit"
                        disabled={isSaving}
                        className="w-full md:w-auto px-10 py-4 bg-secondary text-iron-950 font-epic uppercase text-xs tracking-[0.3em] hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isSaving ? "Binding Changes..." : "Secure Registry"}
                      </button>
                    </div>
                  </form>
                </div>

                {sandboxFundingSection}
              </div>
            )}

            {activeTab === "positions" && (
              <div className="space-y-8">
                <div className="flex items-center justify-between border-b border-iron-800 pb-6">
                  <div>
                    <h2 className="text-xl tracking-[0.2em] font-epic uppercase text-iron-100 mb-2">Active Positions</h2>
                    <p className="text-sm text-iron-500 font-body">Ongoing auctions where you have staked a bid.</p>
                  </div>
                  <div className="flex items-center gap-3 border border-iron-800 bg-iron-900/40 px-4 h-10">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-iron-500">Staked In</span>
                    <span className="text-sm text-secondary font-epic">{activeBidAuctions.length}</span>
                  </div>
                </div>

                {activeBidAuctions.length === 0 ? (
                  <div className="py-24 flex flex-col items-center gap-4 text-center border border-dashed border-iron-800">
                    <div className="w-px h-8 bg-iron-800" />
                    <p className="text-iron-500 font-body italic text-lg">No active bids found. The hall awaits.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Note: In a real app we'd need to fetch card data too, myBidAuctions only returns auctions */}
                    {/* For now we just show a placeholder if card data is missing, or refactor the query */}
                    {activeBidAuctions.map((auction) => (
                      <div key={auction._id} className="border border-iron-800 bg-iron-900/20 p-6 flex flex-col gap-4">
                        <div className="flex justify-between items-start">
                           <div>
                             <h3 className="text-iron-100 font-bold uppercase tracking-wider text-sm">Auction #{auction._id.slice(-6)}</h3>
                             <p className="text-[10px] text-iron-500 uppercase tracking-[0.2em] mt-1">Status: {auction.status}</p>
                           </div>
                           <span className="text-secondary font-epic text-lg">{auction.currentBid} RP</span>
                        </div>
                        <div className="h-px bg-iron-800/50" />

                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === "treasury" && (
              <div className="space-y-8">
                <div className="flex items-center justify-between border-b border-iron-800 pb-6">
                  <div>
                    <h2 className="text-xl tracking-[0.2em] font-epic uppercase text-iron-100 mb-2">My Treasury</h2>
                    <p className="text-sm text-iron-500 font-body">Relics and artifacts you have listed for trade.</p>
                  </div>
                  <div className="flex items-center gap-3 border border-iron-800 bg-iron-900/40 px-4 h-10">
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-iron-500">Listings</span>
                    <span className="text-sm text-secondary font-epic">{!myListings ? 0 : myListings.page.length}</span>
                  </div>
                </div>

                {!myListings || myListings.page.length === 0 ? (
                  <div className="py-24 flex flex-col items-center gap-4 text-center border border-dashed border-iron-800">
                    <div className="w-px h-8 bg-iron-800" />
                    <p className="text-iron-500 font-body italic text-lg">Your treasury is empty. Submit a card to see it here.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* We can use AuctionCard here because listBySeller returns {auction, card} */}
                    {myListings.page.map((item) => (
                       <AuctionCard
                         key={item.auction._id}
                         item={toAuctionListItem({ ...item, minNextBid: item.auction.currentBid })}
                       />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FeedbackBanner({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "success" | "error";
}) {
  const styles =
    tone === "success"
      ? "border-secondary/30 bg-secondary/10 text-secondary"
      : "border-primary/40 bg-primary/10 text-primary";

  return <div className={`border px-4 py-3 text-sm ${styles}`}>{children}</div>;
}
